import { useRef, useState } from 'react';
import { useTexts } from '../../../hooks/texts.hook';
import './style.scss';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import FromToSelector, { type IFromToSelector, type IFromToSelectorState } from '../FromToSelector';
import ValidationInfo from '../../../validation/validation-info';
import type IValidatable from '../validatable.interface';
import { LocationType } from '../../../models/location-type.enum';
import { TranslationService } from '../../../services/translation.service';
import DateTimePicker, { type IDateTimePickerState } from '../DateTimePicker';
import TimeModeSelector from '../TimeModeSelector';
import { TimeMode } from '../TimeModeSelector/time-mode.enum';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import ValidationSummary from '../ValidationSummary';
import { AnalyticsService } from '../../../services/analytics-service';
import type { IJourney } from '../../../models/journey.model';
import { ApiPlaneraResaService } from '../../../services/api-planera-resa/api-planera-resa.service';
import { ErrorResponseHelper } from '../../../helpers/error-response.helper';
import { DateTimeRelatesTo } from '../../../models/date-time-relates-to.enum';
import { JourneyHelper } from '../../../helpers/journey.helper';
import Spinner from '../Spinner';
import PaginatedList from '../JourneySearchResults/PaginatedList';
import Alert from '../Alert';
import { AlertType } from '../Alert/models/alert.model';
import TripLegSelectorResultItem from './components/TripLegSelectorResultItem';
import Summary from './components/Summary';
import type { ITripLeg } from '../../../models/trip-leg.model';
import DateHeader from '../JourneySearchResults/DateHeader';
import { DateHelper } from '../../../helpers/date.helper';
import { type IErrorResponse, ErrorLevel } from '../../../models/error-response.model';
import { ErrorHandlerHelper } from '../../../helpers/error-handler.helper';
import { Status } from '../../../hooks/fetch.hook';
import { DomHelper } from '../../../helpers/dom.helper';
import type { IClientSideStorageItem } from '../../../models/client-side-storage-item.model';
import { IPaginationLinks } from '../../../models/pagination-links.model';
import LogService from '../../../services/log-service/log.service';

export interface ITripLegSelectorState {
  value: {
    journey: IJourney | undefined;
    tripLeg: ITripLeg | undefined;
    tripLegIndex: number | undefined;
  };
  validationInfo: ValidationInfo;
}

interface ITripLegSelectorProps {
  id: string;
  locationTypes?: LocationType[];
  minDateTime?: DateTime;
  maxDateTime?: DateTime;
  selectLegText?: string;
  onlyHistoricTrips?: boolean;
  localStorageItemFrom?: IClientSideStorageItem;
  localStorageItemTo?: IClientSideStorageItem;
  state: ITripLegSelectorState;
  onChange?: ((value: { journey: IJourney | undefined; tripLeg: ITripLeg | undefined; tripLegIndex: number | undefined }) => void) | undefined;
  onEditButtonClick?: () => void;
}

function TripLegSelector({
  id,
  locationTypes = [LocationType.stopArea, LocationType.metaStation],
  minDateTime = DateTime.now().startOf('day').minus({ days: 60 }),
  maxDateTime = DateTime.now().endOf('day').plus({ days: 60 }),
  selectLegText = TranslationService.get('triplegselector.selectleg'),
  onlyHistoricTrips = false,
  localStorageItemFrom = undefined,
  localStorageItemTo = undefined,
  state,
  onChange,
  onEditButtonClick,
}: ITripLegSelectorProps) {
  // === CONFIGURATION ===
  const { t } = useTexts();

  // === STATE ===
  const [fromToSelectorState, setFromToSelectorState] = useState<IFromToSelectorState>({
    value: {
      fromLocation: undefined,
      toLocation: undefined,
    },
    validationInfo: new ValidationInfo(),
  });
  const [dateTimePickerState, setDateTimePickerState] = useState<IDateTimePickerState>({
    value: DateHelper.roundToNearestFiveMinutes(DateTime.now().minus({ hours: 2 })),
    validationInfo: new ValidationInfo(),
  });
  const [timeMode, setTimeMode] = useState<TimeMode>(TimeMode.departure);
  const [searchJourneysStatus, setSearchJourneysStatus] = useState<Status>(Status.notperformed);
  const [receivedJourneys, setReceivedJourneys] = useState<IJourney[]>([]);
  const [receivedPaginationLinks, setReceivedPaginationLinks] = useState<IPaginationLinks | undefined>(undefined);
  const [isSearchingPrevious, setIsSearchingPrevious] = useState<boolean>(false);
  const [isSearchingNext, setIsSearchingNext] = useState<boolean>(false);
  const [isInEditMode, setIsInEditMode] = useState<boolean>(true);
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);

  // === REFS ===
  const fromToSelectorRef = useRef<IFromToSelector>(null!);
  const dateTimePickerRef = useRef<IValidatable>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [{ component: fromToSelectorRef.current, validationInfo: fromToSelectorState.validationInfo, setState: setFromToSelectorState }];
  }

  function filterJourneys(journeys: IJourney[]): IJourney[] {
    const hasTripLegs = (journey: IJourney): boolean => (journey.tripLegs?.length ?? 0) > 0;
    const hasRealtimeLeg = (journey: IJourney): boolean => journey.tripLegs?.some((leg) => leg.serviceJourney.isRealtimeJourney) ?? false;
    const isSelectableJourney = (journey: IJourney): boolean => hasTripLegs(journey) && !JourneyHelper.isWalkJourney(journey) && !hasRealtimeLeg(journey);

    return journeys.filter(isSelectableJourney);
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());
  const uniqueJourneyDates = JourneyHelper.getAllUniqueDates(receivedJourneys);

  // === EVENT HANDLERS ===
  async function handleSearchTripsButtonClick() {
    if (searchJourneysStatus === Status.loading) return;

    const [isFormValid] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      setSearchJourneysStatus(Status.notperformed);
      DomHelper.scrollAndFocus(`${id}-validation-summary`);
      return;
    }

    if (fromToSelectorState.value.fromLocation?.location?.gid === undefined || fromToSelectorState.value.toLocation?.location?.gid === undefined) {
      setSearchJourneysStatus(Status.notperformed);
      return;
    }

    setSearchJourneysStatus(Status.loading);
    fromToSelectorRef.current.addSelectedItemsToAutocompleteHistory();
    setReceivedJourneys([]);
    setReceivedPaginationLinks(undefined);
    AnalyticsService.addEvent(AnalyticsService.TripLegSelector, 'Klick på sök resor', timeMode === TimeMode.departure ? 'Avgångstid' : 'Ankomsttid');
    try {
      const result = onlyHistoricTrips
        ? await ApiPlaneraResaService.getHistoricJourneys(
            fromToSelectorState.value.fromLocation.location?.gid,
            fromToSelectorState.value.toLocation.location?.gid,
            dateTimePickerState.value,
            timeMode === TimeMode.departure ? DateTimeRelatesTo.departure : DateTimeRelatesTo.arrival,
          )
        : await ApiPlaneraResaService.getJourneys(
            fromToSelectorState.value.fromLocation.location?.gid,
            undefined,
            undefined,
            undefined,
            fromToSelectorState.value.toLocation.location?.gid,
            undefined,
            undefined,
            undefined,
            dateTimePickerState.value,
            timeMode === TimeMode.departure ? DateTimeRelatesTo.departure : DateTimeRelatesTo.arrival,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            false,
          );
      const filteredJourneys = filterJourneys(result.results);

      setReceivedJourneys(JourneyHelper.setJourneyIds(filteredJourneys, 0, false));
      setReceivedPaginationLinks(result.links);
      setSearchJourneysStatus(Status.success);
      setErrorResponse(
        onlyHistoricTrips && filteredJourneys.length === 0
          ? { statusCode: 200, errorCode: undefined, errorLevel: ErrorLevel.information, errorMessage: TranslationService.get('triplegselector.searcherror') }
          : undefined,
      );
      if (filteredJourneys.length < 1) {
        AnalyticsService.addEvent(AnalyticsService.TripLegSelector, AnalyticsService.ZeroResultsAction);
      }
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorResponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      AnalyticsService.addEvent(AnalyticsService.TripLegSelector, AnalyticsService.ErrorAction, `Sök delresor - ${AnalyticsService.InternalErrorLabel}`);
      LogService.logError(error, 'Error occurred while searching for journeys in TripLegSelector');
    }
  }

  async function handlePreviousClick(): Promise<void> {
    if (receivedPaginationLinks?.previous === undefined) return;
    try {
      setIsSearchingPrevious(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.previous);
      const filteredJourneys = filterJourneys(result.results);
      setReceivedJourneys((ps) => (ps.length > 0 ? JourneyHelper.setJourneyIds(filteredJourneys, ps[0].id - 1, true).concat(ps) : JourneyHelper.setJourneyIds(filteredJourneys, 0, false)));
      setReceivedPaginationLinks((ps) => (ps ? { ...ps, previous: result.links?.previous } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorResponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      LogService.logError(error, 'Error occurred while fetching previous journeys in TripLegSelector');
    } finally {
      setIsSearchingPrevious(false);
    }
  }

  async function handleNextClick(): Promise<void> {
    if (receivedPaginationLinks?.next === undefined) return;
    try {
      setIsSearchingNext(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.next);
      const filteredJourneys = filterJourneys(result.results);
      setReceivedJourneys((ps) =>
        ps.length > 0 ? ps.concat(JourneyHelper.setJourneyIds(filteredJourneys, ps[ps.length - 1].id + 1, false)) : JourneyHelper.setJourneyIds(filteredJourneys, 0, false),
      );
      setReceivedPaginationLinks((ps) => (ps ? { ...ps, next: result.links?.next } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorResponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      LogService.logError(error, 'Error occurred while fetching next journeys in TripLegSelector');
    } finally {
      setIsSearchingNext(false);
    }
  }

  return (
    <div className="trip-leg-selector">
      <ValidationSummary id={`${id}-validation-summary`} validationSummaryErrors={validationSummaryErrors} />
      <div id={`${id}-form`} className={isInEditMode ? 'd-block' : 'd-none'}>
        <div className="mb-3 m-0">
          <FromToSelector
            id={`${id}-from-to-selector`}
            ref={fromToSelectorRef}
            locationTypes={locationTypes}
            state={fromToSelectorState}
            placeholderText={TranslationService.get('common.stop')}
            localStorageItemFrom={localStorageItemFrom}
            localStorageItemTo={localStorageItemTo}
            enableCurrentPosition={false}
            onChange={(value) => setFromToSelectorState({ value, validationInfo: new ValidationInfo() })}
          />
        </div>
        <div className={isInEditMode ? '' : 'd-none d-md-block'}>
          <div className="mb-3">
            <TimeModeSelector id={`${id}-time-mode-selector`} availableTimeModes={[TimeMode.departure, TimeMode.arrival]} selectedTimeMode={timeMode} onChange={setTimeMode} />
          </div>
          <div className="mb-3 mb-md-4">
            <DateTimePicker
              ref={dateTimePickerRef}
              minDateTime={minDateTime}
              maxDateTime={onlyHistoricTrips ? DateTime.now() : maxDateTime}
              showAsteriskOnRequiredFields={true}
              state={dateTimePickerState}
              onChange={(value) => {
                setDateTimePickerState({ ...dateTimePickerState, value });
              }}
            />
          </div>
          <div className="d-grid">
            <button className="btn btn-primary" disabled={searchJourneysStatus === Status.loading} type="button" onClick={handleSearchTripsButtonClick}>
              {TranslationService.get('common.searchtrip')}
            </button>
          </div>
        </div>
        {isInEditMode && searchJourneysStatus === Status.success && receivedJourneys?.length > 0 && (
          <div className="mt-3">
            <span className={`w-100 d-inline-block text-center fw-bold`}>{selectLegText}</span>
          </div>
        )}
        <Alert
          show={(receivedJourneys.length === 0 && searchJourneysStatus === Status.success) || (searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.information)}
          heading={errorResponse?.errorMessage ?? t('Det finns inga resor på sträckan.', 'There are no trips for the search.')}
          headingTag="div"
          type={AlertType.Default}
          closable={false}
          className="mt-4 text-center"
        />
        <div className="trip-leg-selector__results">
          {searchJourneysStatus === Status.loading && <Spinner className="mt-4" />}
          {searchJourneysStatus === Status.success && (
            <div>
              {receivedJourneys.length > 0 && (
                <>
                  <PaginatedList
                    className="fullwidth-xs"
                    previousButtonDisabled={receivedPaginationLinks?.previous === undefined}
                    nextButtonDisabled={receivedPaginationLinks?.next === undefined}
                    showSpinnerTop={isSearchingPrevious}
                    showSpinnerBottom={isSearchingNext}
                    onPreviousClick={handlePreviousClick}
                    onNextClick={handleNextClick}
                  >
                    {uniqueJourneyDates.map((date) => (
                      <li key={date.toISODate()}>
                        <DateHeader date={date} uniqueJourneyDates={uniqueJourneyDates} />
                        <ul className="list-unstyled">
                          {JourneyHelper.getJourneysForDate(receivedJourneys, date).map((journey, index) => (
                            <TripLegSelectorResultItem
                              key={journey.id}
                              journey={journey}
                              index={index}
                              onSelect={(tripLeg, tripLegIndex) => {
                                setIsInEditMode(false);
                                onChange?.({ journey, tripLeg, tripLegIndex });
                              }}
                            ></TripLegSelectorResultItem>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </PaginatedList>
                </>
              )}
            </div>
          )}
          <Alert
            show={searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.error}
            heading={errorResponse?.errorMessage ?? t.generalError}
            closable={false}
            className="mt-4 text-center"
          />
        </div>
      </div>
      {!isInEditMode && state.value.journey !== undefined && state.value.tripLeg !== undefined && (
        <Summary
          id={id}
          journey={state.value.journey}
          tripLeg={state.value.tripLeg}
          onEditButtonClick={() => {
            flushSync(() => {
              setIsInEditMode(true);
            });
            DomHelper.focusOnFirstFocusableElement(document.getElementById(`${id}-form`) ?? undefined);
            onEditButtonClick?.();
          }}
        />
      )}
    </div>
  );
}

export default TripLegSelector;
